import React, { FC, useMemo, useRef } from "react";
import { t, Trans } from "@helpers/translate";
import { GetStaticPaths, GetStaticProps } from "next";
import { BlogWriterByIdQuery, PostOrderBy, PostsQuery } from "@graphql/types";
import { CACHE_TIME } from "@constants";
import { getMetaDataDescription } from "@helpers/getMetaDataDescription";
import { ShareButton } from "@components/ui/ShareButton";
import LoadingPage from "@components/pages/LoadingPage";
import Head from "@components/common/Head";
import { Alert } from "@components/ui/Alert";
import { Container } from "@components/ui/Container";
import { Breadcrumb } from "@components/ui/Breadcrumb";
import { Title } from "@components/ui/Title";
import { ShowMore } from "@components/ui/ShowMore";
import { Paragraph } from "@components/ui/Paragraph";
import "react-toastify/dist/ReactToastify.css";

import {
  getPostsVariables,
  loader_BlogWriterById,
} from "@components/pages/SupplierPage/loader";

import { Posts } from "@components/pages/SupplierPage/Posts";
import { Avatar } from "@components/ui/Avatar";
import { ROUTES } from "@root/routes";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const blogWriterId = Number(params?.id);
  const res = await loader_BlogWriterById({ id: blogWriterId });

  if ("props" in res) {
    return {
      ...res,
      revalidate: CACHE_TIME, //  1 hour cache
    };
  }

  return res;
};

type Props = {
  initialData?: {
    blogWriter: BlogWriterByIdQuery;
    popularPostsByViews: PostsQuery;
    latestBlogs: PostsQuery;
  };
};

const Page: FC<Props> = ({ initialData }) => {
  const breadRef = useRef<HTMLDivElement | null>(null);

  const blogWriter = initialData?.blogWriter.blogWriterById;

  if (!blogWriter) {
    return <LoadingPage />;
  }

  const breadcrumbData = useMemo(() => {
    const steps = [
      {
        text: t({
          id: "author.blog",
          message: "Blog",
        }),
        href: ROUTES.blog.index(),
      },
      {
        text: t({
          id: "author.breadcrumb.company",
          message: "Company",
        }),
        href: ROUTES.blog.company(),
      },
      {
        text: "Authors",
        href: ROUTES.author(blogWriter.id),
      },
    ];

    return steps;
  }, [breadRef.current?.offsetWidth]);

  return (
    <>
      <Head
        title={t({
          id: "author.seo.title",
          message: `${blogWriter.firstName} ${blogWriter.lastName} - Travel Blog Writer`,
          values: {
            "0": `${blogWriter.firstName}`,
            "1": `${blogWriter.lastName}`,
          },
        })}
        description={getMetaDataDescription(
          "Blogs written by GoWithGuide's travel specialists. GoWithGuide - Private Tours & Local Tour Guides"
        )}
        openGraph={{
          images: [{ url: blogWriter?.picture?.cover || "" }],
        }}
        noindex={blogWriter.isBan}
        nofollow={blogWriter.isBan}
      />

      {blogWriter.isBan && (
        <Alert
          variant="warning"
          className="p-4 mb-10"
          centered={true}
          showIcon={false}
        >
          <Trans id="tour.supplier.status.ban">
            This guide is no longer active on GoWithGuide. We apologize for the
            inconvenience, but please check out other guides and tours in this
            area.
          </Trans>
        </Alert>
      )}

      <Container className="pt-2">
        <div className="flex flex-row relative">
          <div ref={breadRef} className="w-full">
            <Breadcrumb items={breadcrumbData} className="w-full" />
          </div>
          <div className="flex ml-auto">
            <ShareButton
              type="author"
              country={""}
              displayName={blogWriter.displayName}
              url={getAbsoluteUrl(ROUTES.author(blogWriter.id))}
            />
          </div>
        </div>
        <div className="flex flex-col mt-3 md:mt-5">
          <div className="flex flex-wrap mt-8 mb-20">
            <div className="w-full lg:pr-8">
              <div className="flex items-center mb-10">
                <Avatar
                  size="2xl"
                  src={blogWriter.picture?.url}
                  alt={blogWriter.displayName}
                />

                <h2 className="text-secondary lg:text-5xl font-semibold ml-5 md:ml-10 text-xl">
                  {blogWriter.firstName} {blogWriter.lastName}
                </h2>
              </div>

              {/* Biography */}
              {blogWriter.biography && (
                <ShowMore className="mt-2 mb-20">
                  <Title as="h1" size="md" divider>
                    {t({
                      id: "author.block.about.title",
                      message: `About Author`,
                    })}
                  </Title>

                  <Paragraph>{blogWriter.biography}</Paragraph>
                </ShowMore>
              )}

              {!blogWriter.biography && (
                <Title as="h1" size="md" divider>
                  {t({
                    id: "author.block.about.title",
                    message: `About Author`,
                  })}
                </Title>
              )}

              <div className="mobile-negative-margin">
                {/* Popular Blogs */}
                <Posts
                  initialData={initialData?.popularPostsByViews}
                  filters={getPostsVariables(
                    blogWriter.id,
                    4,
                    PostOrderBy.RankingDesc
                  )}
                  showLoadMore={false}
                  postTitle={t({
                    id: "author.block.popular.blogs.title",
                    message: "Popular Blogs",
                  })}
                />

                {/* Latest Blogs */}
                <Posts
                  columnsCount={4}
                  initialData={initialData?.latestBlogs}
                  filters={getPostsVariables(
                    blogWriter.id,
                    4,
                    PostOrderBy.Latest
                  )}
                  postTitle={t({
                    id: "author.latest.blogs.title",
                    message: "Latest Blogs",
                  })}
                />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Page;
